import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Car, Phone, Mail, AlertCircle, CheckCircle, ArrowLeft, Lock } from 'lucide-react';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Card, { CardContent } from '../components/ui/Card';
import { userAPI } from '../services/api';
import appConfig from '../config/appConfig';

const ForgotPassword = () => {
  const [step, setStep] = useState(1); // 1: email, 2: verify OTP, 3: set password, 4: success
  const [loginId, setLoginId] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
  const [otpVerified, setOtpVerified] = useState(false);
  const [verifiedOtpCode, setVerifiedOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Determine if input is email or phone
  const isEmail = loginId.includes('@');

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = { email: loginId };
      const response = await userAPI.forgotPassword(payload);

      if (response.data.success) {
        setSuccess('OTP sent successfully! Please check your email');
        setStep(2);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP only
  const handleVerifyOtp = async () => {
    setError('');
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }
    setVerifiedOtpCode(otpCode);
    setOtpVerified(true);
    setStep(3);
    setSuccess('');
  };

  // Step 3: Reset password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        otp: verifiedOtpCode,
        newPassword,
        email: loginId
      };

      const response = await userAPI.resetPassword(payload);

      if (response.data.success) {
        setSuccess('Password reset successfully!');
        setStep(4);
        // Auto-login and redirect after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <img src={appConfig.logo} alt="Wheels4Rent" className="w-24 h-24 object-contain mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {step === 1 && 'Forgot Password'}
            {step === 2 && 'Verify OTP'}
            {step === 3 && 'Set New Password'}
            {step === 4 && 'Success!'}
          </h1>
          <p className="text-gray-600">
            {step === 1 && 'Enter your email to reset your password'}
            {step === 2 && `Enter the 6-digit OTP sent to ${loginId}`}
            {step === 3 && 'Create your new password'}
            {step === 4 && 'Your password has been reset successfully'}
          </p>
        </div>

        {/* Card */}
        <Card>
          <CardContent className="p-8">
            {/* Step 1: Enter Phone/Email */}
            {step === 1 && (
              <form onSubmit={handleRequestOTP} className="space-y-6">
                <div>
                  <Input
                    label="Email Address"
                    type="email"
                    placeholder="Enter your email address"
                    value={loginId}
                    onChange={(e) => setLoginId(e.target.value)}
                    required
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start animate-pulse">
                    <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-800 font-medium">{error}</p>
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Sending OTP...' : 'Send OTP'}
                </Button>

                <div className="text-center">
                  <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Login
                  </Link>
                </div>
              </form>
            )}

            {/* Step 2: Verify OTP */}
            {step === 2 && (
              <div className="space-y-6">
                {success && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-green-800">{success}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter OTP <span className="text-red-500">*</span>
                  </label>
                  <div className="flex justify-center gap-3">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={otpRefs[index]}
                        type="text"
                        inputMode="numeric"
                        value={digit}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val.length > 1) {
                            const digits = val.replace(/\D/g, '').slice(0, 6).split('');
                            const newOtp = [...otp];
                            digits.forEach((d, i) => { if (index + i < 6) newOtp[index + i] = d; });
                            setOtp(newOtp);
                            otpRefs[Math.min(index + digits.length, 5)]?.current?.focus();
                            return;
                          }
                          if (!/^\d*$/.test(val)) return;
                          const newOtp = [...otp];
                          newOtp[index] = val;
                          setOtp(newOtp);
                          if (val && index < 5) otpRefs[index + 1].current?.focus();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Backspace' && !otp[index] && index > 0) otpRefs[index - 1].current?.focus();
                        }}
                        className="w-11 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-900"
                        maxLength={6}
                        autoComplete="one-time-code"
                        autoFocus={index === 0}
                      />
                    ))}
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start animate-pulse">
                    <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-800 font-medium">{error}</p>
                  </div>
                )}

                <Button type="button" className="w-full" onClick={handleVerifyOtp} disabled={loading || otp.join('').length !== 6}>
                  Verify OTP
                </Button>

                <div className="text-center space-y-2">
                  <button
                    type="button"
                    onClick={() => {
                      setStep(1);
                      setError('');
                      setSuccess('');
                      setOtp(['', '', '', '', '', '']);
                    }}
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                  >
                    Change email
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Set New Password */}
            {step === 3 && (
              <form onSubmit={handleResetPassword} className="space-y-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-green-800">OTP verified! Now set your new password.</p>
                </div>

                <Input
                  label="New Password"
                  type="password"
                  placeholder="Enter new password (min 6 characters)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <Input
                  label="Confirm Password"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-800 font-medium">{error}</p>
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Resetting Password...' : 'Reset Password'}
                </Button>
              </form>
            )}

            {/* Step 4: Success */}
            {step === 4 && (
              <div className="text-center space-y-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <p className="text-gray-600">
                  Redirecting to login...
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-gray-600 mt-8">
          Remember your password?{' '}
          <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
